import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Star, Clock, Sparkles, ChevronLeft, ChevronRight, Plus, Trash2, Wand2, Palette, Edit3, Check, Heart, ArrowRight, Users } from 'lucide-react';

import { SPARK_URL } from './apiConfig';

export default function DateBookingModal({ match, onClose, onDateProposed, counterProposal }) {
  const isCounterProposal = !!counterProposal;

  // ============ WIZARD CONFIG STATE ============
  const [wizardStep, setWizardStep] = useState(isCounterProposal ? null : 'stops');   // 'stops' → 'interests' → 'method' → null (done)
  const [dateStyle, setDateStyle] = useState(null);        // 'single' | 'multi'
  const [interestFocus, setInterestFocus] = useState('shared');  // 'shared' | 'theirs' | 'both'
  const [selectedFocusInterests, setSelectedFocusInterests] = useState([]); // specific interests to weight
  const [method, setMethod] = useState(null);               // 'ai' | 'manual'

  // ============ MAIN FLOW STATE ============
  const [mode, setMode] = useState(isCounterProposal ? 'counter' : 'ai');
  const [step, setStep] = useState(isCounterProposal ? 3 : 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // AI suggestions with batched history
  const [suggestionBatches, setSuggestionBatches] = useState([]);
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [compatibility, setCompatibility] = useState(0);

  // Custom builder
  const [sharedInterests, setSharedInterests] = useState(null);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [customStops, setCustomStops] = useState([
    { id: 1, activity: '', venue: '', time: '', interest: '' }
  ]);

  // Venue & selection
  const [selectedSuggestion, setSelectedSuggestion] = useState(
    isCounterProposal ? {
      dateType: counterProposal.dateType,
      specificEvent: counterProposal.venue?.name || 'Date',
      venueType: counterProposal.dateType,
      isMultiStop: counterProposal.venue?.isMultiStop || false
    } : null
  );
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(
    isCounterProposal && !counterProposal.venue?.isMultiStop ? counterProposal.venue : null
  );
  const [loadingVenues, setLoadingVenues] = useState(false);

  const [multiStops, setMultiStops] = useState(
    isCounterProposal && counterProposal.venue?.isMultiStop && counterProposal.venue?.stops
      ? counterProposal.venue.stops.map((st, i) => ({
          activity: st.activity || `Stop ${i + 1}`, venue: st.venue || '', venueData: null,
          time: st.time || '', searchQuery: st.venue || ''
        }))
      : []
  );
  const [editingStopIdx, setEditingStopIdx] = useState(null);
  const [stopVenues, setStopVenues] = useState([]);
  const [loadingStopVenues, setLoadingStopVenues] = useState(false);

  // Date details — pre-fill from counterProposal
  const cpDate = isCounterProposal && counterProposal.proposedDateTime
    ? new Date(counterProposal.proposedDateTime) : null;
  const [dateTime, setDateTime] = useState({
    date: cpDate ? cpDate.toISOString().split('T')[0] : '',
    time: cpDate ? cpDate.toTimeString().slice(0, 5) : ''
  });
  const cpNotes = counterProposal?.notes || '';
  const [notes, setNotes] = useState(
    isCounterProposal ? cpNotes.replace(/Meeting:.*\n?/,'').replace(/Transportation:.*\n?/,'').replace(/Payment:.*\n?/,'').replace(/\nItinerary:[\s\S]*/,'').trim() : ''
  );
  const [meetingLocation, setMeetingLocation] = useState(
    isCounterProposal && cpNotes.includes('Meet somewhere') ? 'other' : 'venue'
  );
  const [meetingLocationDetails, setMeetingLocationDetails] = useState('');
  const [transportation, setTransportation] = useState(
    isCounterProposal && cpNotes.includes('Pickup') ? 'pickup' : 'own'
  );
  const [pickupLocation, setPickupLocation] = useState('');
  const [payment, setPayment] = useState(
    isCounterProposal && cpNotes.includes('My treat') ? 'ipay' : 'split'
  );

  const currentSuggestions = suggestionBatches[currentBatchIndex] || [];

  // ============ LOAD SHARED INTERESTS (for wizard step 2 and custom builder) ============
  const loadSharedInterests = useCallback(async () => {
    if (sharedInterests) return;
    setLoadingInterests(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${SPARK_URL}/dates/matches/${match.matchId}/shared-interests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setSharedInterests(data.data);
    } catch (err) { console.error('Error loading shared interests:', err); }
    finally { setLoadingInterests(false); }
  }, [match.matchId, sharedInterests]);

  // Load interests when wizard reaches the interests step
  useEffect(() => {
    if (wizardStep === 'interests') loadSharedInterests();
  }, [wizardStep, loadSharedInterests]);

  // ============ LOAD AI SUGGESTIONS ============
  const loadSuggestions = useCallback(async (focusInterests = []) => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('accessToken');
      let url = `${SPARK_URL}/dates/matches/${match.matchId}/suggestions`;
      const params = new URLSearchParams();
      if (dateStyle) params.append('dateStyle', dateStyle);
      if (focusInterests.length > 0) params.append('focusInterests', focusInterests.join(','));
      if (interestFocus === 'theirs') params.append('prioritize', 'theirs');
      if (params.toString()) url += '?' + params.toString();

      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setSuggestionBatches([data.data.suggestions || []]);
        setCurrentBatchIndex(0);
        setCompatibility(data.data.compatibility || 0);
      } else { setError(data.message || 'Failed to load suggestions'); }
    } catch (err) { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  }, [match.matchId, dateStyle, interestFocus]);

  const handleGenerateMore = async () => {
    setRefreshing(true); setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const allPrevious = suggestionBatches.flat().map(s =>
        s.specificEvent || s.description?.substring(0, 80) || s.dateType
      );
      const excludeParam = allPrevious.join('|||');
      let url = `${SPARK_URL}/dates/matches/${match.matchId}/suggestions?exclude=${encodeURIComponent(excludeParam)}`;
      if (dateStyle) url += `&dateStyle=${dateStyle}`;
      if (selectedFocusInterests.length > 0) url += `&focusInterests=${encodeURIComponent(selectedFocusInterests.join(','))}`;
      if (interestFocus === 'theirs') url += '&prioritize=theirs';

      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && data.data.suggestions?.length > 0) {
        const newBatches = [...suggestionBatches, data.data.suggestions];
        setSuggestionBatches(newBatches);
        setCurrentBatchIndex(newBatches.length - 1);
      } else { setError('Could not generate new ideas. Try building a custom date!'); }
    } catch (err) { setError('Network error. Please try again.'); }
    finally { setRefreshing(false); }
  };

  // ============ WIZARD COMPLETION ============
  const handleWizardComplete = () => {
    setWizardStep(null);
    if (method === 'ai') {
      setMode('ai');
      setStep(1);
      loadSuggestions(selectedFocusInterests);
    } else {
      setMode('custom');
      setStep(1);
      // Pre-populate custom stops based on dateStyle
      if (dateStyle === 'multi') {
        setCustomStops([
          { id: 1, activity: '', venue: '', time: '', interest: '' },
          { id: 2, activity: '', venue: '', time: '', interest: '' }
        ]);
      }
    }
  };

  // ============ TOGGLE INTEREST TAG ============
  const toggleFocusInterest = (interest) => {
    setSelectedFocusInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  // ============ EXISTING HANDLERS (preserved) ============
  const deriveAvailableDays = (suggestion) => {
    if (suggestion.availableDays?.length > 0) return suggestion.availableDays;
    const timing = (suggestion.timing || '').toLowerCase();
    if (timing.includes('weekend')) return ['saturday', 'sunday'];
    if (timing.includes('friday') && timing.includes('saturday')) return ['friday', 'saturday'];
    if (timing.includes('saturday') && timing.includes('sunday')) return ['saturday', 'sunday'];
    if (timing.includes('saturday')) return ['saturday'];
    if (timing.includes('sunday')) return ['sunday'];
    if (timing.includes('friday')) return ['friday'];
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
    const found = days.filter(d => timing.includes(d));
    return found.length > 0 ? found : null;
  };

  const parseTimeString = (str) => {
    if (!str) return null;
    if (/^\d{1,2}:\d{2}$/.test(str)) return str.padStart(5, '0');
    const m = str.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM)/);
    if (m) { let h = parseInt(m[1]); const min = m[2] || '00'; if (m[3].toLowerCase() === 'pm' && h < 12) h += 12; if (m[3].toLowerCase() === 'am' && h === 12) h = 0; return `${h.toString().padStart(2,'0')}:${min}`; }
    return null;
  };

  const extractTimeFromSuggestion = (suggestion) => {
    if (suggestion.stops?.length > 0) {
      const firstTime = suggestion.stops[0].time;
      if (firstTime) return parseTimeString(firstTime);
    }
    const desc = suggestion.description || '';
    const timeMatch = desc.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm|AM|PM))/);
    if (timeMatch) return parseTimeString(timeMatch[1]);
    const timing = suggestion.timing || '';
    const rangeMatch = timing.match(/(\d{1,2}(?::\d{2})?\s*(?:am|pm|AM|PM)?)\s*[-–]\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm|AM|PM))/i);
    if (rangeMatch) return parseTimeString(rangeMatch[1].trim().match(/[ap]m/i) ? rangeMatch[1].trim() : rangeMatch[1].trim() + (rangeMatch[2].match(/pm/i) ? 'pm' : 'am'));
    return null;
  };

  const handleSelectSuggestion = async (suggestion) => {
    setSelectedSuggestion(suggestion); setError('');
    const days = deriveAvailableDays(suggestion);
    if (days && !suggestion.availableDays) suggestion.availableDays = days;
    const autoTime = extractTimeFromSuggestion(suggestion);
    if (autoTime) setDateTime(prev => ({ ...prev, time: autoTime }));

    if (suggestion.isMultiStop && suggestion.stops?.length > 0) {
      setMultiStops(suggestion.stops.map((st, i) => ({
        activity: st.activity || st.name || `Stop ${i + 1}`,
        venue: st.venue || '', venueData: null, time: st.time || '',
        searchQuery: st.venue || st.activity + ' Townsville'
      })));
      setStep(2);
    } else {
      setLoadingVenues(true); setStep(2);
      try {
        const token = localStorage.getItem('accessToken');
        const sq = suggestion.searchQuery || suggestion.specificEvent || suggestion.venueType || suggestion.dateType;
        const res = await fetch(`${SPARK_URL}/dates/matches/${match.matchId}/venues`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ dateType: suggestion.dateType, searchQuery: sq })
        });
        const data = await res.json();
        setVenues(data.success ? data.data || [] : []);
      } catch (err) { setVenues([]); }
      finally { setLoadingVenues(false); }
    }
  };

  const handleSelectVenue = (venue) => { setSelectedVenue(venue); setStep(3); };

  const handleEditStopVenue = async (idx) => {
    setEditingStopIdx(idx); setLoadingStopVenues(true); setStopVenues([]);
    try {
      const token = localStorage.getItem('accessToken');
      const stop = multiStops[idx];
      const res = await fetch(`${SPARK_URL}/dates/matches/${match.matchId}/venues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ dateType: 'activity', searchQuery: stop.venue || stop.activity + ' Townsville' })
      });
      const data = await res.json();
      setStopVenues(data.success ? data.data || [] : []);
    } catch (err) { setStopVenues([]); }
    finally { setLoadingStopVenues(false); }
  };

  const handlePickStopVenue = (idx, venue) => {
    const updated = [...multiStops];
    updated[idx] = { ...updated[idx], venue: venue.name, venueData: venue };
    setMultiStops(updated); setEditingStopIdx(null); setStopVenues([]);
  };

  const updateMultiStop = (idx, field, value) => {
    const updated = [...multiStops]; updated[idx] = { ...updated[idx], [field]: value }; setMultiStops(updated);
  };

  const handleMultiStopConfirm = () => {
    const firstTime = multiStops.find(s => s.time)?.time || '';
    if (firstTime) { const p = parseTimeString(firstTime); if (p) setDateTime(prev => ({ ...prev, time: p })); }
    setStep(3);
  };

  const getEventDates = () => {
    if (!selectedSuggestion) return null;
    if (selectedSuggestion.eventDates?.length > 0) return selectedSuggestion.eventDates;
    if (selectedSuggestion.availableDays?.length > 0) return selectedSuggestion.availableDays;
    const derived = deriveAvailableDays(selectedSuggestion);
    if (derived) return derived;
    return null;
  };

  const isDateAllowed = (dateStr) => {
    const ed = getEventDates(); if (!ed) return true;
    if (typeof ed[0] === 'string' && ed[0].includes('-')) return ed.some(d => d.startsWith(dateStr));
    const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    return ed.map(d => d.toLowerCase()).includes(dayNames[new Date(dateStr + 'T12:00:00').getDay()]);
  };

  const handleCustomSubmit = () => {
    const filled = customStops.filter(s => s.activity.trim());
    if (filled.length === 0) { setError('Add at least one activity'); return; }
    const desc = filled.map(s => `${s.time ? s.time + ': ' : ''}${s.activity}${s.venue ? ' at ' + s.venue : ''}`).join(' • ');
    const suggestion = {
      dateType: filled.length > 1 ? 'multi-stop' : 'activity',
      venueType: 'custom',
      specificEvent: filled.map(s => s.activity).join(' → '),
      sharedInterests: filled.map(s => s.interest).filter(Boolean),
      reasoning: 'Custom date you designed from your shared interests',
      matchScore: 95, description: desc,
      searchQuery: filled[0].venue || filled[0].activity + ' Townsville',
      isMultiStop: filled.length > 1, isCustom: true,
      stops: filled.map(s => ({ activity: s.activity, venue: s.venue, time: s.time }))
    };
    handleSelectSuggestion(suggestion);
  };

  const handleSubmit = async () => {
    if (!dateTime.date) { setError('Please select a date'); return; }
    const isMulti = selectedSuggestion?.isMultiStop && multiStops.length > 0;
    if (!isMulti && !dateTime.time) { setError('Please select a time'); return; }
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const timeStr = dateTime.time || '12:00';
      const proposedDateTime = new Date(`${dateTime.date}T${timeStr}`);

      const dateDetails = {
        meetingLocation: meetingLocation === 'venue' ? 'At the venue' : meetingLocationDetails,
        transportation: transportation === 'own' ? 'Own transportation' : `Pickup: ${pickupLocation}`,
        payment: payment === 'split' ? 'Split the bill' : 'My treat'
      };
      let fullNotes = `Meeting: ${dateDetails.meetingLocation}\nTransportation: ${dateDetails.transportation}\nPayment: ${dateDetails.payment}`;
      if (isMulti) {
        fullNotes += '\n\nItinerary:\n' + multiStops.map((s, i) =>
          `Stop ${i + 1}: ${s.time ? s.time + ' - ' : ''}${s.activity}${s.venue ? ' @ ' + s.venue : ''}`
        ).join('\n');
      }
      if (notes) fullNotes += '\n' + notes;

      const defaultLocation = { type: 'Point', coordinates: [146.8003, -19.2590] };

      const venueData = isMulti
        ? { name: selectedSuggestion?.specificEvent || multiStops.map(s => s.activity).join(' → '),
            address: multiStops[0]?.venueData?.address || 'Multiple venues',
            location: multiStops[0]?.venueData?.location || defaultLocation,
            isMultiStop: true,
            stops: multiStops.map(s => ({ activity: s.activity, venue: s.venue, time: s.time,
              ...(s.venueData ? { address: s.venueData.address, placeId: s.venueData.placeId } : {}) })) }
        : selectedVenue
          ? { name: selectedVenue.name, address: selectedVenue.address, placeId: selectedVenue.placeId,
              location: selectedVenue.location || defaultLocation, rating: selectedVenue.rating, priceLevel: selectedVenue.priceLevel,
              photoUrl: selectedVenue.photoUrl }
          : { name: selectedSuggestion?.specificEvent || 'Custom venue', address: 'TBD', location: defaultLocation };

      const payload = {
        dateType: selectedSuggestion?.dateType || counterProposal?.dateType || 'activity',
        venue: venueData,
        proposedDateTime,
        aiSuggestion: isCounterProposal
          ? counterProposal.aiSuggestion
          : { reasoning: selectedSuggestion?.reasoning,
              sharedInterests: selectedSuggestion?.sharedInterests,
              matchScore: selectedSuggestion?.matchScore },
        notes: fullNotes.trim(), dateDetails
      };
      console.log(`📅 ${isCounterProposal ? 'Counter-proposing' : 'Proposing'} date:`, JSON.stringify(payload, null, 2));

      let res;
      if (isCounterProposal) {
        res = await fetch(`${SPARK_URL}/dates/${counterProposal._id}/counter-propose`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${SPARK_URL}/dates/matches/${match.matchId}/propose`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (data.success) { onDateProposed(); onClose(); }
      else { setError(data.message || data.error || 'Failed to propose date'); console.error('Propose error:', data); }
    } catch (err) { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const getTomorrowDate = () => {
    const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0];
  };

  const icons = { adventure:'🪂','multi-stop':'🗺️',dinner:'🍽️',coffee:'☕',drinks:'🍹',outdoor:'🌳',cultural:'🎨',entertainment:'🎬',sports:'⚽',activity:'🎯',wellness:'🧘' };
  const getIcon = (t) => icons[t] || '✨';

  const addStop = () => {
    if (customStops.length >= 5) return;
    setCustomStops([...customStops, { id: Date.now(), activity: '', venue: '', time: '', interest: '' }]);
  };
  const removeStop = (id) => {
    if (customStops.length <= 1) return;
    setCustomStops(customStops.filter(s => s.id !== id));
  };
  const updateStop = (id, field, value) => {
    setCustomStops(customStops.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const filteredSuggestions = currentSuggestions.filter(s => {
    if (dateStyle === 'multi') return s.isMultiStop || s.stops?.length > 1;
    if (dateStyle === 'single') return !s.isMultiStop && (!s.stops || s.stops.length <= 1);
    return true;
  });

  const RadioOption = ({ name, value, current, onChange, label }) => (
    <label style={{
      display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px',
      background: current === value ? '#667eea' : 'white',
      color: current === value ? 'white' : '#2c3e50',
      borderRadius: '8px', border: `2px solid ${current === value ? '#667eea' : '#e9ecef'}`,
      transition: 'all 0.2s'
    }}>
      <input type="radio" name={name} value={value} checked={current === value}
        onChange={(e) => onChange(e.target.value)} style={{ accentColor: '#667eea' }} />
      <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
    </label>
  );

  // ============ WIZARD PROGRESS ============
  const wizardSteps = ['stops', 'interests', 'method'];
  const wizardIndex = wizardSteps.indexOf(wizardStep);
  const wizardLabels = ['Date Style', 'Interests', 'How to Plan'];

  // ============ DETERMINE HEADER TITLE ============
  const getHeaderTitle = () => {
    if (wizardStep) return `Plan a Date with ${match.otherUserName}`;
    if (isCounterProposal) return `Date with ${match.otherUserName}`;
    return `Date with ${match.otherUserName}`;
  };

  // getTheirInterests removed (unused)

  const getOurSharedInterests = () => {
    if (!sharedInterests) return [];
    return sharedInterests.sharedInterests || [];
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10000,padding:'20px' }}>
      <div style={{ background:'white',borderRadius:'20px',maxWidth:'520px',width:'100%',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* ===== HEADER ===== */}
        <div style={{ background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',padding:'20px 24px',color:'white',borderRadius:'20px 20px 0 0',position:'sticky',top:0,zIndex:1 }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <h3 style={{ margin:0,fontSize:'18px',fontWeight:700 }}>
              {wizardStep ? '💜' : '📅'} {getHeaderTitle()}
            </h3>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.2)',border:'none',color:'white',borderRadius:'50%',width:'36px',height:'36px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
              <X size={20} />
            </button>
          </div>

          {/* Wizard progress dots */}
          {wizardStep && (
            <div style={{ display:'flex',gap:'8px',marginTop:'14px',alignItems:'center' }}>
              {wizardSteps.map((ws, i) => (
                <React.Fragment key={ws}>
                  <div style={{
                    display:'flex',alignItems:'center',gap:'6px',
                    opacity: i <= wizardIndex ? 1 : 0.5
                  }}>
                    <div style={{
                      width:'24px',height:'24px',borderRadius:'50%',
                      background: i < wizardIndex ? 'white' : i === wizardIndex ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.25)',
                      color: i <= wizardIndex ? '#667eea' : 'rgba(255,255,255,0.6)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:'11px',fontWeight:700,
                      transition:'all 0.3s'
                    }}>
                      {i < wizardIndex ? '✓' : i + 1}
                    </div>
                    <span style={{ fontSize:'12px',fontWeight:600,color:'rgba(255,255,255,0.9)' }}>
                      {wizardLabels[i]}
                    </span>
                  </div>
                  {i < wizardSteps.length - 1 && (
                    <div style={{ flex:1,height:'2px',background: i < wizardIndex ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',borderRadius:'1px' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Main flow progress bar */}
          {!wizardStep && step >= 1 && !isCounterProposal && (
            <div style={{ display:'flex',gap:'8px',marginTop:'14px' }}>
              {[1,2,3].map(s => <div key={s} style={{ flex:1,height:'4px',borderRadius:'2px',background:step>=s?'white':'rgba(255,255,255,0.3)' }} />)}
            </div>
          )}

          {/* Mode tabs (only on step 1 when wizard is done) */}
          {!wizardStep && step === 1 && !isCounterProposal && (
            <div style={{ display:'flex',gap:'8px',marginTop:'10px' }}>
              <button onClick={() => setMode('ai')} style={{
                flex:1,padding:'8px',borderRadius:'10px',border:'none',fontSize:'12px',fontWeight:600,cursor:'pointer',
                background:mode==='ai'?'white':'rgba(255,255,255,0.2)',color:mode==='ai'?'#667eea':'rgba(255,255,255,0.9)',
                display:'flex',alignItems:'center',justifyContent:'center',gap:'5px'
              }}><Wand2 size={13} /> AI Suggestions</button>
              <button onClick={() => { setMode('custom'); loadSharedInterests(); }} style={{
                flex:1,padding:'8px',borderRadius:'10px',border:'none',fontSize:'12px',fontWeight:600,cursor:'pointer',
                background:mode==='custom'?'white':'rgba(255,255,255,0.2)',color:mode==='custom'?'#667eea':'rgba(255,255,255,0.9)',
                display:'flex',alignItems:'center',justifyContent:'center',gap:'5px'
              }}><Palette size={13} /> Build Your Own</button>
            </div>
          )}
        </div>

        {error && <div style={{ background:'#fee',color:'#c33',padding:'12px 24px',fontSize:'14px' }}>{error}</div>}

        <div style={{ padding:'24px' }}>

          {/* ================================================================ */}
          {/* WIZARD STEP 1: Single or Multi-Stop?                            */}
          {/* ================================================================ */}
          {wizardStep === 'stops' && (
            <div style={{ animation:'fadeIn 0.3s ease' }}>
              <div style={{ textAlign:'center',marginBottom:'24px' }}>
                <div style={{ fontSize:'36px',marginBottom:'12px' }}>🗓️</div>
                <h4 style={{ fontSize:'20px',fontWeight:700,color:'#2c3e50',marginBottom:'6px' }}>What kind of date?</h4>
                <p style={{ fontSize:'14px',color:'#7f8c8d',margin:0 }}>Keep it simple or plan an adventure</p>
              </div>

              <div style={{ display:'flex',flexDirection:'column',gap:'12px',marginBottom:'24px' }}>
                <button onClick={() => setDateStyle('single')} style={{
                  padding:'20px',borderRadius:'16px',border: dateStyle === 'single' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: dateStyle === 'single' ? 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',
                  boxShadow: dateStyle === 'single' ? '0 4px 16px rgba(102,126,234,0.15)' : 'none'
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                    <div style={{ width:'48px',height:'48px',borderRadius:'14px',
                      background: dateStyle === 'single' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f0f2f5',
                      display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0 }}>
                      🎯
                    </div>
                    <div>
                      <div style={{ fontSize:'16px',fontWeight:700,color:'#2c3e50',marginBottom:'4px' }}>Single Stop</div>
                      <div style={{ fontSize:'13px',color:'#7f8c8d',lineHeight:'1.4' }}>One great venue — dinner, drinks, an activity, or something special</div>
                    </div>
                    {dateStyle === 'single' && <Check size={20} style={{ color:'#667eea',flexShrink:0,marginLeft:'auto' }} />}
                  </div>
                </button>

                <button onClick={() => setDateStyle('multi')} style={{
                  padding:'20px',borderRadius:'16px',border: dateStyle === 'multi' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: dateStyle === 'multi' ? 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',
                  boxShadow: dateStyle === 'multi' ? '0 4px 16px rgba(102,126,234,0.15)' : 'none'
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                    <div style={{ width:'48px',height:'48px',borderRadius:'14px',
                      background: dateStyle === 'multi' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f0f2f5',
                      display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0 }}>
                      🗺️
                    </div>
                    <div>
                      <div style={{ fontSize:'16px',fontWeight:700,color:'#2c3e50',marginBottom:'4px' }}>Multi-Stop</div>
                      <div style={{ fontSize:'13px',color:'#7f8c8d',lineHeight:'1.4' }}>A journey — beach walk then sunset cocktails, or adventure then dinner</div>
                    </div>
                    {dateStyle === 'multi' && <Check size={20} style={{ color:'#667eea',flexShrink:0,marginLeft:'auto' }} />}
                  </div>
                </button>
              </div>

              <button onClick={() => { if (dateStyle) setWizardStep('interests'); }}
                disabled={!dateStyle}
                style={{
                  width:'100%',padding:'16px',
                  background: dateStyle ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e9ecef',
                  color: dateStyle ? 'white' : '#95a5a6',
                  border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:600,
                  cursor: dateStyle ? 'pointer' : 'not-allowed',
                  display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'
                }}>
                Next <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* ================================================================ */}
          {/* WIZARD STEP 2: Interest Focus                                    */}
          {/* ================================================================ */}
          {wizardStep === 'interests' && (
            <div style={{ animation:'fadeIn 0.3s ease' }}>
              <button onClick={() => setWizardStep('stops')} style={{ background:'none',border:'none',color:'#667eea',cursor:'pointer',fontSize:'14px',marginBottom:'12px',fontWeight:600,padding:0 }}>
                ← Back
              </button>

              <div style={{ textAlign:'center',marginBottom:'20px' }}>
                <div style={{ fontSize:'36px',marginBottom:'12px' }}>💜</div>
                <h4 style={{ fontSize:'20px',fontWeight:700,color:'#2c3e50',marginBottom:'6px' }}>Focus on interests?</h4>
                <p style={{ fontSize:'14px',color:'#7f8c8d',margin:0 }}>
                  Tailor the date around what {match.otherUserName} loves, what you share, or both
                </p>
              </div>

              {/* Interest focus mode */}
              <div style={{ display:'flex',flexDirection:'column',gap:'10px',marginBottom:'20px' }}>
                <button onClick={() => setInterestFocus('shared')} style={{
                  padding:'14px 16px',borderRadius:'12px',border: interestFocus === 'shared' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: interestFocus === 'shared' ? 'rgba(102,126,234,0.06)' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',display:'flex',alignItems:'center',gap:'12px'
                }}>
                  <Users size={20} style={{ color: interestFocus === 'shared' ? '#667eea' : '#7f8c8d',flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>What we both like</div>
                    <div style={{ fontSize:'12px',color:'#7f8c8d' }}>Focus on shared interests</div>
                  </div>
                  {interestFocus === 'shared' && <Check size={16} style={{ color:'#667eea',marginLeft:'auto' }} />}
                </button>

                <button onClick={() => setInterestFocus('theirs')} style={{
                  padding:'14px 16px',borderRadius:'12px',border: interestFocus === 'theirs' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: interestFocus === 'theirs' ? 'rgba(102,126,234,0.06)' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',display:'flex',alignItems:'center',gap:'12px'
                }}>
                  <Heart size={20} style={{ color: interestFocus === 'theirs' ? '#667eea' : '#7f8c8d',flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>Impress them</div>
                    <div style={{ fontSize:'12px',color:'#7f8c8d' }}>Prioritise {match.otherUserName}'s interests</div>
                  </div>
                  {interestFocus === 'theirs' && <Check size={16} style={{ color:'#667eea',marginLeft:'auto' }} />}
                </button>

                <button onClick={() => setInterestFocus('both')} style={{
                  padding:'14px 16px',borderRadius:'12px',border: interestFocus === 'both' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: interestFocus === 'both' ? 'rgba(102,126,234,0.06)' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',display:'flex',alignItems:'center',gap:'12px'
                }}>
                  <Sparkles size={20} style={{ color: interestFocus === 'both' ? '#667eea' : '#7f8c8d',flexShrink:0 }} />
                  <div>
                    <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>Mix it up</div>
                    <div style={{ fontSize:'12px',color:'#7f8c8d' }}>Blend shared + their unique interests</div>
                  </div>
                  {interestFocus === 'both' && <Check size={16} style={{ color:'#667eea',marginLeft:'auto' }} />}
                </button>
              </div>

              {/* Browsable interest tags */}
              {loadingInterests ? (
                <div style={{ textAlign:'center',padding:'20px',color:'#7f8c8d',fontSize:'13px' }}>
                  Loading interests...
                </div>
              ) : sharedInterests && (
                <div style={{ marginBottom:'20px' }}>
                  <div style={{ fontSize:'13px',fontWeight:600,color:'#2c3e50',marginBottom:'10px' }}>
                    Tap to focus on specific interests <span style={{ color:'#7f8c8d',fontWeight:400 }}>(optional)</span>
                  </div>

                  {/* Shared interests */}
                  {getOurSharedInterests().length > 0 && (
                    <div style={{ marginBottom:'12px' }}>
                      <div style={{ fontSize:'11px',fontWeight:600,color:'#667eea',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.5px' }}>
                        💜 Shared
                      </div>
                      <div style={{ display:'flex',gap:'6px',flexWrap:'wrap' }}>
                        {getOurSharedInterests().map((int, i) => (
                          <button key={i} onClick={() => toggleFocusInterest(int)} style={{
                            padding:'6px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:500,cursor:'pointer',
                            border: selectedFocusInterests.includes(int) ? '2px solid #667eea' : '1.5px solid #e0e0e0',
                            background: selectedFocusInterests.includes(int) ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'white',
                            color: selectedFocusInterests.includes(int) ? 'white' : '#555',
                            transition:'all 0.2s',
                            boxShadow: selectedFocusInterests.includes(int) ? '0 2px 8px rgba(102,126,234,0.3)' : 'none'
                          }}>
                            {int}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Their unique interests */}
                  {(sharedInterests.uniqueToThem || sharedInterests.user2Unique || []).length > 0 && (
                    <div>
                      <div style={{ fontSize:'11px',fontWeight:600,color:'#e91e63',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.5px' }}>
                        ❤️ {match.otherUserName}'s interests
                      </div>
                      <div style={{ display:'flex',gap:'6px',flexWrap:'wrap' }}>
                        {(sharedInterests.uniqueToThem || sharedInterests.user2Unique || []).map((int, i) => (
                          <button key={i} onClick={() => toggleFocusInterest(int)} style={{
                            padding:'6px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:500,cursor:'pointer',
                            border: selectedFocusInterests.includes(int) ? '2px solid #e91e63' : '1.5px solid #e0e0e0',
                            background: selectedFocusInterests.includes(int) ? 'linear-gradient(135deg, #e91e63, #c2185b)' : 'white',
                            color: selectedFocusInterests.includes(int) ? 'white' : '#555',
                            transition:'all 0.2s',
                            boxShadow: selectedFocusInterests.includes(int) ? '0 2px 8px rgba(233,30,99,0.3)' : 'none'
                          }}>
                            {int}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedFocusInterests.length > 0 && (
                    <div style={{ marginTop:'12px',padding:'10px 14px',background:'#f8f4ff',borderRadius:'10px',fontSize:'12px',color:'#667eea',fontWeight:500 }}>
                      Focusing on: {selectedFocusInterests.join(', ')}
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => setWizardStep('method')}
                style={{
                  width:'100%',padding:'16px',
                  background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color:'white',border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:600,
                  cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'
                }}>
                Next <ArrowRight size={16} />
              </button>
            </div>
          )}

          {/* ================================================================ */}
          {/* WIZARD STEP 3: AI or Manual?                                     */}
          {/* ================================================================ */}
          {wizardStep === 'method' && (
            <div style={{ animation:'fadeIn 0.3s ease' }}>
              <button onClick={() => setWizardStep('interests')} style={{ background:'none',border:'none',color:'#667eea',cursor:'pointer',fontSize:'14px',marginBottom:'12px',fontWeight:600,padding:0 }}>
                ← Back
              </button>

              <div style={{ textAlign:'center',marginBottom:'24px' }}>
                <div style={{ fontSize:'36px',marginBottom:'12px' }}>✨</div>
                <h4 style={{ fontSize:'20px',fontWeight:700,color:'#2c3e50',marginBottom:'6px' }}>How do you want to plan it?</h4>
                <p style={{ fontSize:'14px',color:'#7f8c8d',margin:0 }}>Let AI do the work or craft it yourself</p>
              </div>

              <div style={{ display:'flex',flexDirection:'column',gap:'12px',marginBottom:'24px' }}>
                <button onClick={() => setMethod('ai')} style={{
                  padding:'20px',borderRadius:'16px',border: method === 'ai' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: method === 'ai' ? 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',
                  boxShadow: method === 'ai' ? '0 4px 16px rgba(102,126,234,0.15)' : 'none'
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                    <div style={{ width:'48px',height:'48px',borderRadius:'14px',
                      background: method === 'ai' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f0f2f5',
                      display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Wand2 size={22} style={{ color: method === 'ai' ? 'white' : '#7f8c8d' }} />
                    </div>
                    <div>
                      <div style={{ fontSize:'16px',fontWeight:700,color:'#2c3e50',marginBottom:'4px' }}>AI Suggest</div>
                      <div style={{ fontSize:'13px',color:'#7f8c8d',lineHeight:'1.4' }}>
                        Get creative date ideas tailored to
                        {selectedFocusInterests.length > 0 ? ' your selected interests' : ' your shared vibes'}
                      </div>
                    </div>
                    {method === 'ai' && <Check size={20} style={{ color:'#667eea',flexShrink:0,marginLeft:'auto' }} />}
                  </div>
                </button>

                <button onClick={() => setMethod('manual')} style={{
                  padding:'20px',borderRadius:'16px',border: method === 'manual' ? '2px solid #667eea' : '2px solid #e9ecef',
                  background: method === 'manual' ? 'linear-gradient(135deg, rgba(102,126,234,0.06), rgba(118,75,162,0.06))' : 'white',
                  cursor:'pointer',textAlign:'left',transition:'all 0.2s',
                  boxShadow: method === 'manual' ? '0 4px 16px rgba(102,126,234,0.15)' : 'none'
                }}>
                  <div style={{ display:'flex',alignItems:'center',gap:'14px' }}>
                    <div style={{ width:'48px',height:'48px',borderRadius:'14px',
                      background: method === 'manual' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f0f2f5',
                      display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                      <Palette size={22} style={{ color: method === 'manual' ? 'white' : '#7f8c8d' }} />
                    </div>
                    <div>
                      <div style={{ fontSize:'16px',fontWeight:700,color:'#2c3e50',marginBottom:'4px' }}>Build It Myself</div>
                      <div style={{ fontSize:'13px',color:'#7f8c8d',lineHeight:'1.4' }}>
                        Handpick each activity and venue — you're the planner
                      </div>
                    </div>
                    {method === 'manual' && <Check size={20} style={{ color:'#667eea',flexShrink:0,marginLeft:'auto' }} />}
                  </div>
                </button>
              </div>

              {/* Summary of choices */}
              <div style={{ background:'#f8f9fa',borderRadius:'12px',padding:'14px 16px',marginBottom:'20px' }}>
                <div style={{ fontSize:'12px',fontWeight:600,color:'#7f8c8d',marginBottom:'8px',textTransform:'uppercase',letterSpacing:'0.5px' }}>Your Plan</div>
                <div style={{ display:'flex',gap:'8px',flexWrap:'wrap' }}>
                  <span style={{ background:'white',padding:'5px 10px',borderRadius:'8px',fontSize:'12px',fontWeight:600,color:'#2c3e50',border:'1px solid #e9ecef' }}>
                    {dateStyle === 'single' ? '🎯 Single Stop' : '🗺️ Multi-Stop'}
                  </span>
                  <span style={{ background:'white',padding:'5px 10px',borderRadius:'8px',fontSize:'12px',fontWeight:600,color:'#2c3e50',border:'1px solid #e9ecef' }}>
                    {interestFocus === 'shared' ? '💜 Shared' : interestFocus === 'theirs' ? '❤️ Impress' : '✨ Mixed'}
                  </span>
                  {selectedFocusInterests.length > 0 && (
                    <span style={{ background:'#f0f0ff',padding:'5px 10px',borderRadius:'8px',fontSize:'12px',fontWeight:500,color:'#667eea' }}>
                      +{selectedFocusInterests.length} interests
                    </span>
                  )}
                </div>
              </div>

              <button onClick={() => { if (method) handleWizardComplete(); }}
                disabled={!method}
                style={{
                  width:'100%',padding:'16px',
                  background: method ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e9ecef',
                  color: method ? 'white' : '#95a5a6',
                  border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:600,
                  cursor: method ? 'pointer' : 'not-allowed',
                  display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'
                }}>
                {method === 'ai' ? <><Wand2 size={16} /> Get Suggestions</> : <><Palette size={16} /> Start Building</>}
              </button>
            </div>
          )}

          {/* ================================================================ */}
          {/* STEP 1: AI SUGGESTIONS                                           */}
          {/* ================================================================ */}
          {!wizardStep && step === 1 && mode === 'ai' && (<div>
            {compatibility > 0 && (
              <div style={{ display:'flex',alignItems:'center',gap:'10px',padding:'12px 16px',marginBottom:'16px',borderRadius:'12px',
                background:compatibility>=80?'rgba(81,207,102,0.08)':'rgba(255,149,0,0.08)',
                border:`1px solid ${compatibility>=80?'rgba(81,207,102,0.25)':'rgba(255,149,0,0.25)'}` }}>
                <div style={{ width:'40px',height:'40px',borderRadius:'50%',background:compatibility>=80?'#51cf66':'#ff9500',
                  color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:700 }}>{compatibility}%</div>
                <div>
                  <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>{compatibility>=80?'Great Match!':'Good Match'}</div>
                  <div style={{ fontSize:'12px',color:'#7f8c8d' }}>Based on shared interests</div>
                </div>
              </div>
            )}

            {/* Config summary pill */}
            <div style={{ display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'14px' }}>
              <span style={{ background:'#f0f0ff',padding:'4px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:600,color:'#667eea' }}>
                {dateStyle === 'single' ? '🎯 Single' : '🗺️ Multi-Stop'}
              </span>
              {selectedFocusInterests.length > 0 && (
                <span style={{ background:'#fce4ec',padding:'4px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:600,color:'#e91e63' }}>
                  💜 {selectedFocusInterests.length} interests focused
                </span>
              )}
              <button onClick={() => { setWizardStep('stops'); setSuggestionBatches([]); }}
                style={{ background:'none',border:'none',color:'#667eea',fontSize:'11px',fontWeight:600,cursor:'pointer',padding:'4px 8px',textDecoration:'underline' }}>
                Edit
              </button>
            </div>

            {/* Batch nav */}
            {suggestionBatches.length > 1 && (
              <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:'16px',marginBottom:'14px' }}>
                <button onClick={() => setCurrentBatchIndex(i => Math.max(0,i-1))} disabled={currentBatchIndex===0}
                  style={{ background:'none',border:'2px solid #e9ecef',borderRadius:'50%',width:'34px',height:'34px',cursor:currentBatchIndex===0?'not-allowed':'pointer',
                    display:'flex',alignItems:'center',justifyContent:'center',opacity:currentBatchIndex===0?0.4:1 }}>
                  <ChevronLeft size={16} />
                </button>
                <span style={{ fontSize:'13px',color:'#7f8c8d',fontWeight:600 }}>
                  Ideas {currentBatchIndex+1} of {suggestionBatches.length}
                </span>
                <button onClick={() => setCurrentBatchIndex(i => Math.min(suggestionBatches.length-1,i+1))} disabled={currentBatchIndex===suggestionBatches.length-1}
                  style={{ background:'none',border:'2px solid #e9ecef',borderRadius:'50%',width:'34px',height:'34px',
                    cursor:currentBatchIndex===suggestionBatches.length-1?'not-allowed':'pointer',
                    display:'flex',alignItems:'center',justifyContent:'center',opacity:currentBatchIndex===suggestionBatches.length-1?0.4:1 }}>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign:'center', padding:'32px 20px', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, pointerEvents:'none' }}>
                  {['💕','✨','🌹','💫','🦋','💗'].map((e, i) => (
                    <span key={i} className="romantic-bubble" style={{ position:'absolute', bottom:'0', fontSize: i % 2 === 0 ? '16px' : '12px', left: `${15 + i * 13}%` }}>{e}</span>
                  ))}
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', marginBottom:'20px' }}>
                  {['💜','💗','❤️','💗','💜'].map((h, i) => (
                    <span key={i} className="romantic-loader-heart" style={{ fontSize: i === 2 ? '32px' : i === 1 || i === 3 ? '22px' : '16px', display:'inline-block' }}>{h}</span>
                  ))}
                </div>
                <p className="shimmer-text" style={{ fontSize:'16px', fontWeight:700, marginBottom:'6px' }}>Finding the perfect date...</p>
                <p style={{ color:'#94a3b8', fontSize:'13px', margin:0 }}>Our AI is matching your vibes ✨</p>
                <div style={{ marginTop:'20px', height:'3px', borderRadius:'2px', background:'#f1f5f9', overflow:'hidden', maxWidth:'200px', marginLeft:'auto', marginRight:'auto' }}>
                  <div style={{ height:'100%', borderRadius:'2px', background:'linear-gradient(90deg, #f43f5e, #a855f7, #6366f1)', backgroundSize:'200% 100%', animation:'shimmer 1.5s ease-in-out infinite' }} />
                </div>
              </div>
            ) : (<>
              {filteredSuggestions.length === 0 ? (
                <div style={{ textAlign:'center',padding:'30px 20px',color:'#7f8c8d' }}>
                  <p style={{ fontSize:'15px',marginBottom:'8px' }}>No {dateStyle === 'multi' ? 'multi-stop' : 'single stop'} suggestions yet</p>
                  <p style={{ fontSize:'13px' }}>Try generating more ideas or switch to Build Your Own</p>
                </div>
              ) : (
              <div style={{ display:'flex',flexDirection:'column',gap:'14px' }}>
                {filteredSuggestions.map((s, i) => (
                  <div key={`${currentBatchIndex}-${i}`} onClick={() => handleSelectSuggestion(s)}
                    style={{ padding:'18px',border:'2px solid #e9ecef',borderRadius:'16px',cursor:'pointer',transition:'all 0.3s',background:'white' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#667eea'; e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 20px rgba(102,126,234,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='#e9ecef'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'10px' }}>
                      <div style={{ fontSize:'16px',fontWeight:600,color:'#2c3e50',flex:1 }}>
                        {getIcon(s.dateType)} {s.specificEvent || s.dateType}
                      </div>
                      <div style={{ background:s.matchScore>=80?'#e8f5e9':'#fff3e0',color:s.matchScore>=80?'#2e7d32':'#f57c00',
                        padding:'4px 10px',borderRadius:'12px',fontSize:'12px',fontWeight:600,whiteSpace:'nowrap',marginLeft:'8px' }}>
                        {s.matchScore}%
                      </div>
                    </div>
                    {s.isMultiStop && <div style={{ display:'inline-flex',alignItems:'center',gap:'4px',background:'linear-gradient(135deg,#667eea,#764ba2)',color:'white',padding:'3px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:600,marginBottom:'10px',marginRight:'6px' }}>🗺️ Multi-Stop Date</div>}
                    {s.timing && <div style={{ display:'inline-flex',alignItems:'center',gap:'4px',background:'#f0f4ff',color:'#667eea',padding:'3px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:600,marginBottom:'10px',marginRight:'6px' }}>🕐 {s.timing}</div>}
                    {(s.availableDays?.length > 0 || (s.timing && s.timing.toLowerCase().includes('weekend'))) && <div style={{ display:'inline-flex',alignItems:'center',gap:'4px',background:'#fff8f0',color:'#e67e22',padding:'3px 10px',borderRadius:'8px',fontSize:'11px',fontWeight:600,marginBottom:'10px' }}>📅 {s.availableDays ? s.availableDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ') : 'Weekends'}</div>}
                    {s.timing && <div style={{ fontSize:'12px',color:'#667eea',marginBottom:'8px',display:'flex',alignItems:'center',gap:'5px' }}><Clock size={13} /> {s.timing}</div>}
                    <p style={{ fontSize:'13px',color:'#555',marginBottom:'10px',lineHeight:'1.6',margin:'0 0 10px 0' }}>{s.description || s.reasoning}</p>
                    {s.stops?.length > 0 && (
                      <div style={{ background:'#f8f4ff',padding:'12px',borderRadius:'10px',marginBottom:'10px' }}>
                        {s.stops.map((st, si) => (
                          <div key={si} style={{ display:'flex',alignItems:'center',gap:'8px',fontSize:'12px',color:'#444',
                            paddingBottom:si<s.stops.length-1?'8px':0,marginBottom:si<s.stops.length-1?'8px':0,
                            borderBottom:si<s.stops.length-1?'1px dashed #ddd':'none' }}>
                            <div style={{ width:'22px',height:'22px',borderRadius:'50%',background:'#667eea',color:'white',fontSize:'11px',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,flexShrink:0 }}>{si+1}</div>
                            <div><span style={{ fontWeight:600 }}>{st.time && `${st.time} `}</span>{st.activity}{st.venue?` — ${st.venue}`:''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {s.sharedInterests?.length > 0 && (
                      <div style={{ display:'flex',gap:'5px',flexWrap:'wrap' }}>
                        {s.sharedInterests.slice(0,4).map((int, ii) => (
                          <span key={ii} style={{ background:'#f0f0ff',padding:'3px 8px',borderRadius:'10px',fontSize:'11px',color:'#667eea',fontWeight:500 }}>{int}</span>
                        ))}
                      </div>
                    )}
                    {s.isAiGenerated && <div style={{ marginTop:'10px',padding:'6px 10px',background:'linear-gradient(135deg,rgba(102,126,234,0.08),rgba(118,75,162,0.08))',borderRadius:'8px',fontSize:'11px',color:'#667eea',fontWeight:600,display:'flex',alignItems:'center',gap:'5px' }}><Sparkles size={12} /> AI-Powered</div>}
                    {s.isFallback && <div style={{ marginTop:'10px',padding:'6px 10px',background:'#f8f9fa',borderRadius:'8px',fontSize:'11px',color:'#7f8c8d',fontWeight:600 }}>☕ Curated</div>}
                  </div>
                ))}
              </div>
              )}
              <button onClick={handleGenerateMore} disabled={refreshing}
                style={{ width:'100%',marginTop:'16px',padding:'14px',background:refreshing?'linear-gradient(135deg, #fce4ec 0%, #f3e5f5 100%)':'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color:refreshing?'#e91e63':'white',border:refreshing?'2px solid #f8bbd0':'none',borderRadius:'12px',fontSize:'14px',fontWeight:600,
                  cursor:refreshing?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',
                  transition:'all 0.3s' }}>
                {refreshing ? (
                  <>
                    <span style={{ display:'inline-flex', gap:'4px' }}>
                      {['💗','💜','💗'].map((h,i) => (
                        <span key={i} style={{ animation:`float-heart 1.2s ease-in-out infinite`, animationDelay:`${i*0.2}s`, fontSize:'14px' }}>{h}</span>
                      ))}
                    </span>
                    Dreaming up more ideas...
                  </>
                ) : (
                  <><Sparkles size={16} /> ✨ Generate More Ideas</>
                )}
              </button>
            </>)}
          </div>)}

          {/* ================================================================ */}
          {/* STEP 1: CUSTOM BUILDER                                           */}
          {/* ================================================================ */}
          {!wizardStep && step === 1 && mode === 'custom' && (<div>
            <h4 style={{ fontSize:'17px',fontWeight:600,marginBottom:'6px',color:'#2c3e50' }}>Build Your Date {dateStyle === 'multi' ? 'Journey' : ''}</h4>
            <p style={{ fontSize:'13px',color:'#7f8c8d',marginBottom:'20px' }}>
              {dateStyle === 'multi' ? 'Map out your perfect multi-stop date' : 'Pick an activity and venue'}
            </p>

            {/* Config summary */}
            {selectedFocusInterests.length > 0 && (
              <div style={{ background:'#f8f4ff',padding:'10px 14px',borderRadius:'10px',marginBottom:'16px',fontSize:'12px',color:'#667eea',fontWeight:500 }}>
                Focused interests: {selectedFocusInterests.join(', ')}
              </div>
            )}

            {/* Shared interests */}
            {loadingInterests ? (
              <div style={{ textAlign:'center',padding:'20px',color:'#7f8c8d',fontSize:'13px' }}>Loading shared interests...</div>
            ) : sharedInterests && (
              <div style={{ background:'#f8f4ff',padding:'14px',borderRadius:'12px',marginBottom:'20px' }}>
                <div style={{ fontSize:'13px',fontWeight:600,color:'#2c3e50',marginBottom:'8px' }}>💜 What you both like:</div>
                {(sharedInterests.sharedInterests || []).length > 0 ? (
                  <div style={{ display:'flex',gap:'6px',flexWrap:'wrap' }}>
                    {(sharedInterests.sharedInterests || []).map((int, i) => (
                      <span key={i} onClick={() => {
                        const emptyStop = customStops.find(s => !s.interest);
                        if (emptyStop) updateStop(emptyStop.id, 'interest', int);
                      }} style={{
                        background:'white',padding:'5px 10px',borderRadius:'10px',fontSize:'12px',color:'#667eea',fontWeight:500,
                        border:'1px solid rgba(102,126,234,0.2)',cursor:'pointer',transition:'all 0.2s'
                      }}>{int}</span>
                    ))}
                  </div>
                ) : (
                  <p style={{ fontSize:'12px',color:'#7f8c8d',margin:0 }}>
                    No exact matches, but you share categories: {sharedInterests.sharedCategories?.join(', ') || 'Try exploring new things together!'}
                  </p>
                )}
              </div>
            )}

            {/* Stop builder */}
            <div style={{ marginBottom:'20px' }}>
              {customStops.map((stop, idx) => (
                <div key={stop.id} style={{
                  display:'flex',gap:'10px',marginBottom:'12px',padding:'14px',background:'#f8f9fa',borderRadius:'12px',
                  border:'1px solid #e9ecef',position:'relative'
                }}>
                  <div style={{ width:'28px',height:'28px',borderRadius:'50%',background:'linear-gradient(135deg,#667eea,#764ba2)',
                    color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:700,flexShrink:0,marginTop:'2px' }}>
                    {idx+1}
                  </div>
                  <div style={{ flex:1 }}>
                    <input type="text" placeholder="Activity (e.g. Beach walk, Cocktails)" value={stop.activity}
                      onChange={e => updateStop(stop.id, 'activity', e.target.value)}
                      style={{ width:'100%',padding:'10px',border:'2px solid #e9ecef',borderRadius:'8px',fontSize:'14px',outline:'none',marginBottom:'8px',boxSizing:'border-box' }} />
                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px' }}>
                      <input type="text" placeholder="Venue (optional)" value={stop.venue}
                        onChange={e => updateStop(stop.id, 'venue', e.target.value)}
                        style={{ padding:'8px',border:'2px solid #e9ecef',borderRadius:'8px',fontSize:'13px',outline:'none' }} />
                      <input type="text" placeholder="Time (e.g. 4pm)" value={stop.time}
                        onChange={e => updateStop(stop.id, 'time', e.target.value)}
                        style={{ padding:'8px',border:'2px solid #e9ecef',borderRadius:'8px',fontSize:'13px',outline:'none' }} />
                    </div>
                    {stop.interest && (
                      <div style={{ marginTop:'6px',display:'inline-flex',alignItems:'center',gap:'4px',background:'#f0f0ff',padding:'3px 8px',borderRadius:'8px',fontSize:'11px',color:'#667eea' }}>
                        💜 {stop.interest}
                      </div>
                    )}
                  </div>
                  {customStops.length > 1 && (
                    <button onClick={() => removeStop(stop.id)} style={{ background:'none',border:'none',color:'#e74c3c',cursor:'pointer',padding:'4px',flexShrink:0 }}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}

              {(dateStyle === 'multi' || customStops.length > 1) && customStops.length < 5 && (
                <button onClick={addStop} style={{
                  width:'100%',padding:'12px',border:'2px dashed #d0d0d0',borderRadius:'12px',background:'transparent',
                  color:'#7f8c8d',fontSize:'13px',fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'
                }}>
                  <Plus size={16} /> Add another stop
                </button>
              )}
            </div>

            <button onClick={handleCustomSubmit}
              disabled={!customStops.some(s => s.activity.trim())}
              style={{
                width:'100%',padding:'16px',
                background:customStops.some(s => s.activity.trim())?'linear-gradient(135deg, #667eea 0%, #764ba2 100%)':'#e9ecef',
                color:customStops.some(s => s.activity.trim())?'white':'#95a5a6',
                border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:600,
                cursor:customStops.some(s => s.activity.trim())?'pointer':'not-allowed'
              }}>
              🗺️ Find Venues for This Date
            </button>
          </div>)}

          {/* ================================================================ */}
          {/* STEP 2: SINGLE VENUE                                             */}
          {/* ================================================================ */}
          {!wizardStep && step === 2 && !selectedSuggestion?.isMultiStop && (<div>
            <button onClick={() => { setStep(1); setSelectedSuggestion(null); }} style={{ background:'none',border:'none',color:'#667eea',cursor:'pointer',fontSize:'14px',marginBottom:'16px',fontWeight:600 }}>← Back</button>
            <h4 style={{ fontSize:'18px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}>Choose a Venue</h4>
            <p style={{ fontSize:'14px',color:'#7f8c8d',marginBottom:'16px' }}>For: {selectedSuggestion?.specificEvent || selectedSuggestion?.dateType}</p>
            <div style={{ marginBottom:'20px',background:'#f8f4ff',padding:'16px',borderRadius:'12px' }}>
              <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}><Clock size={14} style={{ verticalAlign:'middle',marginRight:'6px' }} />What time?</label>
              {dateTime.time && selectedSuggestion?.timing && (
                <div style={{ fontSize:'12px',color:'#667eea',marginBottom:'8px',fontStyle:'italic' }}>
                  💡 Suggested: {selectedSuggestion.timing}
                </div>
              )}
              <input type="time" value={dateTime.time} onChange={e => setDateTime({...dateTime, time:e.target.value})} style={{ padding:'12px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'14px',outline:'none',width:'100%',boxSizing:'border-box' }} />
            </div>
            {loadingVenues ? (
              <div style={{ textAlign:'center', padding:'32px 20px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'16px' }}>
                  {['📍','💫','🏠'].map((e,i) => (
                    <span key={i} className="romantic-loader-heart" style={{ fontSize:'24px', display:'inline-block' }}>{e}</span>
                  ))}
                </div>
                <p className="shimmer-text" style={{ fontSize:'15px', fontWeight:600, marginBottom:'4px' }}>Scouting the best spots...</p>
                <p style={{ color:'#94a3b8', fontSize:'13px', margin:0 }}>Finding somewhere special nearby</p>
              </div>
            ) : venues.length === 0 ? (
              <div style={{ textAlign:'center',padding:'40px',color:'#95a5a6' }}>
                <p>No venues found nearby</p>
                <button onClick={() => setStep(3)} style={{ marginTop:'16px',padding:'12px 24px',background:'#667eea',color:'white',border:'none',borderRadius:'10px',cursor:'pointer',fontWeight:600 }}>
                  Continue Without Venue
                </button>
              </div>
            ) : (
              <div style={{ display:'flex',flexDirection:'column',gap:'12px' }}>
                {venues.map((v, i) => (
                  <div key={i} onClick={() => handleSelectVenue(v)}
                    style={{ padding:'16px',border:'2px solid #e9ecef',borderRadius:'12px',cursor:'pointer',transition:'all 0.3s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='#667eea'; e.currentTarget.style.boxShadow='0 4px 12px rgba(102,126,234,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='#e9ecef'; e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ display:'flex',gap:'12px' }}>
                      {v.photoUrl && <img src={v.photoUrl} alt={v.name} style={{ width:'80px',height:'80px',borderRadius:'8px',objectFit:'cover' }} />}
                      <div style={{ flex:1 }}>
                        <h5 style={{ margin:'0 0 6px',fontSize:'16px',fontWeight:600,color:'#2c3e50' }}>{v.name}</h5>
                        <div style={{ display:'flex',alignItems:'center',gap:'12px',marginBottom:'6px' }}>
                          {v.rating > 0 && <div style={{ display:'flex',alignItems:'center',gap:'4px',fontSize:'13px',color:'#666' }}><Star size={14} fill="#ffc107" color="#ffc107" />{v.rating}</div>}
                          {v.priceLevel && <div style={{ fontSize:'13px',color:'#666' }}>{v.priceLevel}</div>}
                          {v.distance && <div style={{ display:'flex',alignItems:'center',gap:'4px',fontSize:'13px',color:'#666' }}><MapPin size={14} />{v.distance}km</div>}
                        </div>
                        <p style={{ margin:0,fontSize:'12px',color:'#7f8c8d',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{v.address}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>)}

          {/* ================================================================ */}
          {/* STEP 2: MULTI ITINERARY                                          */}
          {/* ================================================================ */}
          {!wizardStep && step === 2 && selectedSuggestion?.isMultiStop && (<div>
            <button onClick={() => { setStep(1); setSelectedSuggestion(null); setMultiStops([]); }} style={{ background:'none',border:'none',color:'#667eea',cursor:'pointer',fontSize:'14px',marginBottom:'16px',fontWeight:600 }}>← Back</button>
            <h4 style={{ fontSize:'18px',fontWeight:600,marginBottom:'4px',color:'#2c3e50' }}>Plan Your Itinerary</h4>
            <p style={{ fontSize:'13px',color:'#7f8c8d',marginBottom:'20px' }}>Customise each stop — change venues, adjust times</p>
            <div style={{ display:'flex',flexDirection:'column',gap:'16px',marginBottom:'24px' }}>
              {multiStops.map((stop,idx) => (
                <div key={idx} style={{ border:'2px solid #e9ecef',borderRadius:'14px',overflow:'hidden' }}>
                  <div style={{ background:'#f8f4ff',padding:'14px 16px',display:'flex',alignItems:'center',gap:'10px' }}>
                    <div style={{ width:'28px',height:'28px',borderRadius:'50%',background:'linear-gradient(135deg,#667eea,#764ba2)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:700,flexShrink:0 }}>{idx+1}</div>
                    <div style={{ flex:1 }}><div style={{ fontSize:'15px',fontWeight:600,color:'#2c3e50' }}>{stop.activity}</div>{stop.venue && <div style={{ fontSize:'12px',color:'#7f8c8d',marginTop:'2px' }}>📍 {stop.venue}</div>}</div>
                  </div>
                  <div style={{ padding:'14px 16px' }}>
                    <div style={{ display:'grid',gridTemplateColumns:'1fr auto',gap:'10px',marginBottom:'10px' }}>
                      <div><label style={{ fontSize:'11px',color:'#7f8c8d',fontWeight:600,marginBottom:'4px',display:'block' }}>Time</label>
                        <input type="text" placeholder="e.g. 5:30pm" value={stop.time} onChange={e => updateMultiStop(idx,'time',e.target.value)} style={{ width:'100%',padding:'10px',border:'2px solid #e9ecef',borderRadius:'8px',fontSize:'14px',outline:'none',boxSizing:'border-box' }} /></div>
                      <div style={{ display:'flex',alignItems:'flex-end' }}>
                        <button onClick={() => editingStopIdx===idx ? setEditingStopIdx(null) : handleEditStopVenue(idx)} style={{ padding:'10px 14px',borderRadius:'8px',border:'2px solid #667eea',background:editingStopIdx===idx?'#667eea':'white',color:editingStopIdx===idx?'white':'#667eea',fontSize:'13px',fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',whiteSpace:'nowrap' }}>
                          {editingStopIdx===idx ? <><Check size={14} /> Done</> : <><Edit3 size={14} /> Change Venue</>}
                        </button></div>
                    </div>
                    {editingStopIdx===idx && (<div style={{ marginTop:'8px',border:'1px solid #e9ecef',borderRadius:'10px',overflow:'hidden' }}>
                      {loadingStopVenues ? <div style={{ padding:'20px',textAlign:'center',color:'#7f8c8d',fontSize:'13px' }}>Searching venues...</div>
                      : stopVenues.length===0 ? <div style={{ padding:'16px',textAlign:'center',color:'#95a5a6',fontSize:'13px' }}>No venues found</div>
                      : <div style={{ maxHeight:'200px',overflowY:'auto' }}>{stopVenues.slice(0,5).map((v,vi) => (
                          <div key={vi} onClick={() => handlePickStopVenue(idx,v)} style={{ padding:'12px 14px',cursor:'pointer',borderBottom:vi<4?'1px solid #f0f0f0':'none' }}
                            onMouseEnter={e=>e.currentTarget.style.background='#f8f4ff'} onMouseLeave={e=>e.currentTarget.style.background='white'}>
                            <div style={{ display:'flex',gap:'10px',alignItems:'center' }}>
                              {v.photoUrl && <img src={v.photoUrl} alt="" style={{ width:'48px',height:'48px',borderRadius:'6px',objectFit:'cover' }} />}
                              <div style={{ flex:1 }}><div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>{v.name}</div>
                                <div style={{ fontSize:'12px',color:'#7f8c8d',marginTop:'2px' }}>{v.rating>0 && <><Star size={11} fill="#ffc107" color="#ffc107" /> {v.rating} · </>}{v.address?.substring(0,35)}</div></div>
                            </div></div>))}</div>}
                    </div>)}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={handleMultiStopConfirm} disabled={!multiStops.some(s=>s.time)} style={{ width:'100%',padding:'16px',background:multiStops.some(s=>s.time)?'linear-gradient(135deg,#667eea,#764ba2)':'#e9ecef',color:multiStops.some(s=>s.time)?'white':'#95a5a6',border:'none',borderRadius:'12px',fontSize:'15px',fontWeight:600,cursor:multiStops.some(s=>s.time)?'pointer':'not-allowed' }}>📅 Choose a Date →</button>
            {!multiStops.some(s=>s.time) && <p style={{ textAlign:'center',fontSize:'12px',color:'#e67e22',marginTop:'8px' }}>Set a time for at least one stop to continue</p>}
          </div>)}

          {/* ================================================================ */}
          {/* STEP 3: DATE + DETAILS                                           */}
          {/* ================================================================ */}
          {!wizardStep && step === 3 && (<div>
            {!isCounterProposal && (
              <button onClick={() => setStep(2)} style={{ background:'none',border:'none',color:'#667eea',cursor:'pointer',fontSize:'14px',marginBottom:'16px',fontWeight:600 }}>← Back</button>
            )}

            {/* Counter-proposal banner */}
            {isCounterProposal && (
              <div style={{ background:'linear-gradient(135deg,#fff8e1,#fff3cd)',border:'2px solid #ffc107',borderRadius:'14px',padding:'16px',marginBottom:'20px' }}>
                <div style={{ fontSize:'15px',fontWeight:700,color:'#856404',marginBottom:'8px' }}>✏️ Suggest Changes</div>
                <div style={{ fontSize:'13px',color:'#856404',lineHeight:'1.5',marginBottom:'10px' }}>
                  Edit any details below — the date, time, venue, or logistics. Your changes will be sent back for approval.
                </div>
                <div style={{ background:'white',borderRadius:'10px',padding:'12px',fontSize:'13px',color:'#555' }}>
                  <strong>Original plan:</strong> {counterProposal.venue?.name || 'Date'}<br/>
                  📅 {new Date(counterProposal.proposedDateTime).toLocaleString('en-AU', { weekday:'short', day:'numeric', month:'short', hour:'numeric', minute:'2-digit' })}
                  {counterProposal.venue?.address && <><br/>📍 {counterProposal.venue.address}</>}
                </div>
              </div>
            )}

            <h4 style={{ fontSize:'18px',fontWeight:600,marginBottom:'20px',color:'#2c3e50' }}>
              {isCounterProposal ? 'Your Proposed Changes' : 'Pick a Date'}
            </h4>

            {/* Venue change option for counter-proposals */}
            {isCounterProposal && (
              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'10px',color:'#2c3e50' }}>📍 Venue</label>
                <div style={{ background:'#f8f9fa',padding:'14px',borderRadius:'12px',marginBottom:'10px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50' }}>{selectedVenue?.name || counterProposal.venue?.name || 'Venue'}</div>
                    <div style={{ fontSize:'12px',color:'#7f8c8d' }}>{selectedVenue?.address || counterProposal.venue?.address || ''}</div>
                  </div>
                  <button onClick={async () => {
                    setLoadingVenues(true);
                    try {
                      const token = localStorage.getItem('accessToken');
                      const sq = counterProposal.dateType || 'restaurant';
                      const res = await fetch(`${SPARK_URL}/dates/matches/${match.matchId}/venues`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ dateType: counterProposal.dateType, searchQuery: sq })
                      });
                      const data = await res.json();
                      setVenues(data.success ? data.data || [] : []);
                    } catch (err) { setVenues([]); }
                    finally { setLoadingVenues(false); }
                  }}
                  style={{ padding:'8px 14px',background:'#667eea',color:'white',border:'none',borderRadius:'8px',fontSize:'12px',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap' }}>
                    🔄 Change Venue
                  </button>
                </div>
                {venues.length > 0 && (
                  <div style={{ maxHeight:'200px',overflowY:'auto',display:'flex',flexDirection:'column',gap:'8px' }}>
                    {venues.map((v,i) => (
                      <div key={i} onClick={() => { setSelectedVenue(v); setVenues([]); }}
                        style={{ background:'white',border:'2px solid #e9ecef',borderRadius:'10px',padding:'12px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                        <div>
                          <div style={{ fontSize:'13px',fontWeight:600,color:'#2c3e50' }}>{v.name}</div>
                          <div style={{ fontSize:'11px',color:'#7f8c8d' }}>{v.address}</div>
                        </div>
                        {v.rating > 0 && <span style={{ fontSize:'12px',color:'#f59e0b' }}>⭐ {v.rating}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {loadingVenues && <div style={{ textAlign:'center',padding:'12px',color:'#7f8c8d',fontSize:'13px' }}>Searching venues...</div>}
              </div>
            )}

            {/* Time change for counter-proposals */}
            {isCounterProposal && (
              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}>🕐 Time</label>
                <input type="time" value={dateTime.time} onChange={e => setDateTime({...dateTime, time:e.target.value})}
                  style={{ padding:'14px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'16px',outline:'none',width:'100%',boxSizing:'border-box' }} />
              </div>
            )}

            {/* Summary */}
            {selectedSuggestion?.isMultiStop && multiStops.length > 0 ? (
              <div style={{ background:'#f8f4ff',padding:'16px',borderRadius:'12px',marginBottom:'20px' }}>
                <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'8px' }}>🗺️ Your Itinerary</div>
                {multiStops.map((s,i) => (
                  <div key={i} style={{ fontSize:'13px',color:'#444',display:'flex',gap:'8px',alignItems:'center',marginBottom:i<multiStops.length-1?'6px':0 }}>
                    <span style={{ background:'#667eea',color:'white',width:'20px',height:'20px',borderRadius:'50%',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:700,flexShrink:0 }}>{i+1}</span>
                    <span>{s.time && <strong>{s.time}</strong>} {s.activity}{s.venue ? ` @ ${s.venue}` : ''}</span>
                  </div>
                ))}
              </div>
            ) : selectedVenue ? (
              <div style={{ background:'#f8f9fa',padding:'16px',borderRadius:'12px',marginBottom:'20px' }}>
                <div style={{ fontSize:'14px',fontWeight:600,color:'#2c3e50',marginBottom:'4px' }}>{selectedVenue.name}</div>
                <div style={{ fontSize:'12px',color:'#7f8c8d' }}>{selectedVenue.address}</div>
                {dateTime.time && <div style={{ fontSize:'12px',color:'#667eea',marginTop:'6px' }}><Clock size={12} style={{ verticalAlign:'middle' }} /> {dateTime.time}</div>}
              </div>
            ) : null}

            {/* Date picker */}
            <div style={{ marginBottom:'20px' }}>
              <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}>📅 Select a Date</label>
              {getEventDates() && (
                <div style={{ background:'#fff8f0',border:'1px solid #ffe0b2',borderRadius:'10px',padding:'10px 14px',marginBottom:'12px',fontSize:'13px',color:'#e67e22' }}>
                  ⚠️ {(() => {
                    const ed = getEventDates();
                    if (!ed) return 'Limited availability';
                    if (typeof ed[0] === 'string' && ed[0].includes('-')) return `Only available on: ${ed.join(', ')}`;
                    return `Only available on: ${ed.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}`;
                  })()}
                </div>
              )}
              <input type="date" min={getTomorrowDate()} value={dateTime.date}
                onChange={e => { if(isDateAllowed(e.target.value)){setDateTime({...dateTime,date:e.target.value});setError('');}else{setError("This event isn't available on that date.");}}}
                style={{ padding:'14px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'16px',outline:'none',width:'100%',boxSizing:'border-box' }} />
              {!dateTime.time && !selectedSuggestion?.isMultiStop && (
                <div style={{ marginTop:'12px' }}>
                  <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}>🕐 Select a Time</label>
                  <input type="time" value={dateTime.time} onChange={e => setDateTime({...dateTime,time:e.target.value})}
                    style={{ padding:'14px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'16px',outline:'none',width:'100%',boxSizing:'border-box' }} />
                </div>
              )}
            </div>

            {/* Meeting / Transport / Payment */}
            <div style={{ background:'#f8f9fa',padding:'20px',borderRadius:'16px',marginBottom:'24px' }}>
              <h4 style={{ fontSize:'16px',fontWeight:600,margin:'0 0 16px',color:'#2c3e50' }}>Date Details</h4>

              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'10px',color:'#2c3e50' }}>📍 Where to meet?</label>
                <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
                  <RadioOption name="meetingLocation" value="venue" current={meetingLocation} onChange={setMeetingLocation} label="Meet at the venue" />
                  <RadioOption name="meetingLocation" value="other" current={meetingLocation} onChange={setMeetingLocation} label="Meet somewhere else" />
                </div>
                {meetingLocation === 'other' && (
                  <input type="text" placeholder="Where? (e.g., Flinders Street entrance)" value={meetingLocationDetails}
                    onChange={e => setMeetingLocationDetails(e.target.value)}
                    style={{ width:'100%',marginTop:'10px',padding:'12px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'14px',outline:'none',boxSizing:'border-box' }} />
                )}
              </div>

              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'10px',color:'#2c3e50' }}>🚗 How will we get there?</label>
                <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
                  <RadioOption name="transport" value="own" current={transportation} onChange={v => { setTransportation(v); setPickupLocation(''); }} label="We'll make our own way" />
                  <RadioOption name="transport" value="pickup" current={transportation} onChange={setTransportation} label="I can pick you up" />
                </div>
                {transportation === 'pickup' && (
                  <input type="text" placeholder="Where to pick up?" value={pickupLocation}
                    onChange={e => setPickupLocation(e.target.value)}
                    style={{ width:'100%',marginTop:'10px',padding:'12px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'14px',outline:'none',boxSizing:'border-box' }} />
                )}
              </div>

              <div>
                <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'10px',color:'#2c3e50' }}>💳 How should we handle the bill?</label>
                <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
                  <RadioOption name="payment" value="split" current={payment} onChange={setPayment} label="Split the bill" />
                  <RadioOption name="payment" value="ipay" current={payment} onChange={setPayment} label="My treat!" />
                </div>
              </div>
            </div>

            <div style={{ marginBottom:'24px' }}>
              <label style={{ display:'block',fontSize:'14px',fontWeight:600,marginBottom:'8px',color:'#2c3e50' }}>Additional Notes (Optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any special requests or details..."
                style={{ width:'100%',padding:'12px',border:'2px solid #e9ecef',borderRadius:'10px',fontSize:'14px',outline:'none',minHeight:'80px',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box' }} />
            </div>

            <button onClick={handleSubmit} disabled={loading || !dateTime.date}
              style={{
                width:'100%',padding:'16px',
                background:(loading||!dateTime.date)?'#e9ecef':'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color:(loading||!dateTime.date)?'#95a5a6':'white',
                border:'none',borderRadius:'12px',fontSize:'16px',fontWeight:600,
                cursor:(loading||!dateTime.date)?'not-allowed':'pointer'
              }}>
              {loading ? 'Sending...' : isCounterProposal ? '💬 Send Suggested Changes' : '📅 Propose Date'}
            </button>
          </div>)}

        </div>
      </div>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes fadeIn { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes float-heart {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.7; }
          50% { transform: translateY(-18px) scale(1.15); opacity: 1; }
        }
        @keyframes drift-up {
          0% { transform: translateY(30px) scale(0.5); opacity: 0; }
          20% { opacity: 1; }
          100% { transform: translateY(-60px) scale(0.8); opacity: 0; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.25); }
          50% { box-shadow: 0 0 24px 8px rgba(244,63,94,0.15); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes typewriter {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        .romantic-loader-heart {
          animation: float-heart 2s ease-in-out infinite;
        }
        .romantic-loader-heart:nth-child(2) { animation-delay: 0.3s; }
        .romantic-loader-heart:nth-child(3) { animation-delay: 0.6s; }
        .romantic-loader-heart:nth-child(4) { animation-delay: 0.9s; }
        .romantic-loader-heart:nth-child(5) { animation-delay: 1.2s; }
        .romantic-bubble {
          animation: drift-up 2.5s ease-out infinite;
        }
        .romantic-bubble:nth-child(1) { animation-delay: 0s; left: 20%; }
        .romantic-bubble:nth-child(2) { animation-delay: 0.5s; left: 40%; }
        .romantic-bubble:nth-child(3) { animation-delay: 1s; left: 60%; }
        .romantic-bubble:nth-child(4) { animation-delay: 1.5s; left: 80%; }
        .romantic-bubble:nth-child(5) { animation-delay: 0.7s; left: 35%; }
        .romantic-bubble:nth-child(6) { animation-delay: 1.3s; left: 70%; }
        .shimmer-text {
          background: linear-gradient(90deg, #94a3b8 0%, #f43f5e 40%, #a855f7 60%, #94a3b8 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }
      `}</style>
    </div>
  );
}